import { Avatar, AvatarFallback, AvatarImage } from "@loot-os/ui/components/avatar";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuGroup,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@loot-os/ui/components/dropdown-menu";
import { SidebarMenu, SidebarMenuButton, SidebarMenuItem } from "@loot-os/ui/components/sidebar";
import { Link, useParams } from "@tanstack/react-router";
import { Check, ChevronsUpDown, LogOut, Search } from "lucide-react";
import { Fragment } from "react";

import { initials } from "../lib/format";
import { useScopeStore } from "../lib/scopeStore";
import {
  canSwitchScope,
  type ScopeIdentity,
  type ScopeRow,
  scopeIdentity,
  scopeRows,
} from "../lib/scopeSwitcher";
import { commandPaletteShortcutLabel } from "../lib/useCommandPaletteHotkey";
import { useTenantRole } from "../lib/useTenantRole";

interface SessionUser {
  name?: string | null;
  email: string;
  image?: string | null;
}

/** The platform's mark, or a tenant's initials. */
function ScopeMark({
  identity,
  className,
}: {
  identity: Pick<ScopeIdentity, "name" | "tenant">;
  className: string;
}) {
  if (identity.tenant) {
    return (
      <Avatar className={`shrink-0 rounded-lg ${className}`}>
        <AvatarFallback className="rounded-lg">{initials(identity.name)}</AvatarFallback>
      </Avatar>
    );
  }
  return (
    <span
      aria-hidden
      className={`flex shrink-0 items-center justify-center rounded-lg bg-primary font-bold text-primary-foreground ${className}`}
    >
      L
    </span>
  );
}

function ScopeText({ identity }: { identity: ScopeIdentity }) {
  return (
    <div className="grid flex-1 text-left text-sm leading-tight group-data-[collapsible=icon]:hidden">
      <span className="truncate font-medium">{identity.name}</span>
      {identity.tier && (
        <span className="truncate text-xs text-muted-foreground">{identity.tier}</span>
      )}
    </div>
  );
}

/** Who is signed in: the menu's heading. */
function UserIdentity({ user }: { user: SessionUser }) {
  const monogram = initials(user.name?.trim() || user.email);
  return (
    <div className="flex items-center gap-2 px-1 py-1.5 text-left text-sm">
      <Avatar className="size-8 rounded-lg">
        {user.image && <AvatarImage src={user.image} alt={user.name ?? user.email} />}
        <AvatarFallback className="rounded-lg">{monogram}</AvatarFallback>
      </Avatar>
      <div className="grid flex-1 text-left text-sm leading-tight">
        <span className="truncate font-medium">{user.name || user.email}</span>
        <span className="truncate text-xs text-muted-foreground">{user.email}</span>
      </div>
    </div>
  );
}

function ScopeRowItem({ row, onSearch }: { row: ScopeRow; onSearch: () => void }) {
  switch (row.kind) {
    case "platform":
      return (
        <DropdownMenuItem render={<Link to="/" />}>
          <ScopeMark identity={{ name: "Loot OS", tenant: false }} className="size-6 text-xs" />
          Platform
          {row.current && <Check className="ml-auto size-4" />}
        </DropdownMenuItem>
      );
    case "tenant":
      return (
        <DropdownMenuItem
          render={<Link to="/tenants/$tenantId" params={{ tenantId: row.tenant.id }} />}
        >
          <ScopeMark
            identity={{ name: row.tenant.name, tenant: true }}
            className="size-6 text-xs"
          />
          <span className="truncate">{row.tenant.name}</span>
          {row.tenant.archivedAt && <span className="text-xs text-muted-foreground">archived</span>}
          {row.current && <Check className="ml-auto size-4" />}
        </DropdownMenuItem>
      );
    case "search":
      return (
        <DropdownMenuItem onClick={onSearch}>
          <Search />
          Switch tenant…
          <kbd className="ml-auto rounded border border-border bg-muted px-1.5 font-mono text-[10px] text-muted-foreground">
            {commandPaletteShortcutLabel()}
          </kbd>
        </DropdownMenuItem>
      );
  }
}

/**
 * The sidebar header, and the sidebar's one menu: which scope the caller is in — the platform,
 * or one tenant — with who they are signed in as above the rows that move between scopes and
 * "Log out" below them. Inside a tenant the sidebar body is that tenant's pages alone, so this
 * block is where the tenant's name lives; the environment row sits under Search
 * (`EnvironmentSwitcher`). `onSearch` opens the ⌘K palette, which is how a platform admin reaches any
 * other tenant. A member inside their one tenant has nowhere else to go, so their menu is the
 * identity and "Log out" alone.
 */
export function ScopeSwitcher({
  user,
  onSearch,
  onSignOut,
}: {
  user: SessionUser;
  onSearch: () => void;
  onSignOut: () => void;
}) {
  const { tenantId } = useParams({ strict: false }) as { tenantId?: string };
  const { isPlatformAdmin, tenants } = useTenantRole();
  const lastTenantId = useScopeStore((s) => s.lastTenantId);
  const input = { isPlatformAdmin, tenants, tenantId: tenantId ?? null, lastTenantId };
  const identity = scopeIdentity(input);
  const rows = scopeRows(input);

  return (
    <SidebarMenu>
      <SidebarMenuItem>
        <DropdownMenu>
          <DropdownMenuTrigger
            render={
              <SidebarMenuButton
                size="lg"
                className="data-[popup-open]:bg-sidebar-accent data-[popup-open]:text-sidebar-accent-foreground"
              />
            }
          >
            <ScopeMark identity={identity} className="size-8" />
            <ScopeText identity={identity} />
            {/* The visible label is the scope, so name what else the menu holds. */}
            <span className="sr-only">Account and scope menu</span>
            <ChevronsUpDown className="ml-auto size-4 group-data-[collapsible=icon]:hidden" />
          </DropdownMenuTrigger>
          <DropdownMenuContent
            className="w-(--anchor-width) min-w-56 rounded-lg"
            side="right"
            align="start"
            sideOffset={4}
          >
            {/* Base UI's label reads a group context, so it needs the group even alone. */}
            <DropdownMenuGroup>
              <DropdownMenuLabel className="p-0 font-normal">
                <UserIdentity user={user} />
              </DropdownMenuLabel>
            </DropdownMenuGroup>
            {canSwitchScope(rows) && (
              <>
                <DropdownMenuSeparator />
                {rows.map((row, i) => (
                  <Fragment key={row.kind === "tenant" ? row.tenant.id : row.kind}>
                    {row.kind === "search" && i > 0 && <DropdownMenuSeparator />}
                    <ScopeRowItem row={row} onSearch={onSearch} />
                  </Fragment>
                ))}
              </>
            )}
            <DropdownMenuSeparator />
            <DropdownMenuItem onClick={onSignOut}>
              <LogOut />
              Log out
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </SidebarMenuItem>
    </SidebarMenu>
  );
}
